// モフトレチェックAPI.

import axios, { AxiosInstance } from 'axios';
import { Dispatch } from 'redux';
import * as AWS from '../constants/AWS';
import * as MoffCheckConst from '../constants/MoffCheck';

/**
 * MoffCheckクラス.
 *
 * @export
 * @class MoffCheck
 */
export class MoffCheck {
  /** インスタンス */
  private static _instance: MoffCheck;
  private axiosInstance: AxiosInstance;

  /** インスタンスの取得 */
  public static get instance(): MoffCheck | null {
    // _inctanceが存在しない場合に、new MoffAPI()を実行する。
    if (!this._instance) {
      this._instance = new MoffCheck();
      
      // .envの設定 
      const API_BASE_URL = String(process.env.REACT_APP_MOFF_CHECK_API_URL);
      const API_KEY = String(process.env.REACT_APP_MOFF_CHECK_API_KEY);

      if (API_BASE_URL === 'undefined') {
        alert('.envにREACT_APP_MOFF_CHECK_API_URLの設定がありません.');
        return null;
      }

      if (API_KEY === 'undefined') {
        alert('.envにREACT_APP_MOFF_CHECK_API_KEYの設定がありません.');
        return null;
      }

      const userPool = AWS.userPool();
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser === null) {
        return null;
      }

      const self = this;
      cognitoUser.getSession(function(err: any, session: any) {
        if (err) {
          return;
        }

        if (session) {
          self._instance.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            headers: {
              'Authorization': session.getIdToken().getJwtToken(),
              'x-api-key': API_KEY
            }
          });
        }
      });
    }

    // 生成済みのインスタンスを返す
    return this._instance;
  }

  /**
   * TUG測定データ.
   * 特定期間内の件数,内訳を取得.
   *
   * @param {string} uniqueId
   * @param {string} startDate
   * @param {string} endDate
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffCheck
   */
  public getTUGRecords(
    uniqueId: string, startDate: string, endDate: string, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    const url = `/check/walking/tug/${uniqueId}/records/from/${startDate}/to/${endDate}`;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());

      this.axiosInstance.get(url)
        .then(function(response: any) {
          dispatch(fetchedFunc(MoffCheckConst.SUCCESS, response.data));
        })
        .catch(function(error: any) {
          self.showAxiosError('getTUGRecords', error);
          dispatch(fetchedFunc(MoffCheckConst.ERROR, error));
        });
    };
  }

  /**
   * TUG測定データ.
   * 最終利用日.
   *
   * @param {string} uniqueId
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffCheck
   */
  public getTUGRecordsLatest(
    uniqueId: string, requestFunc: Function, fetchedFunc: Function): any {
    // const self = this;
    const url = `/check/walking/tug/${uniqueId}/records/latest`;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());

      this.axiosInstance.get(url)
        .then(function(response: any) {
          dispatch(fetchedFunc(MoffCheckConst.SUCCESS, response.data));
        })
        .catch(function(error: any) {
          // 404のとき無視.
          // self.showAxiosError('getTUGRecordsLatest', error);
          dispatch(fetchedFunc(MoffCheckConst.ERROR, error));
        });
    };
  }

  /**
   * バランス測定データ.
   * (開眼片足立ち).
   *
   * @param {string} uniqueId
   * @param {string} startDate
   * @param {string} endDate
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffCheck
   */
  public getBalanceRecords(
    uniqueId: string, startDate: string, endDate: string, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    const url = `/check/balance/${uniqueId}/records/from/${startDate}/to/${endDate}`;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get(url)
        .then(function(response: any) {
          dispatch(fetchedFunc(MoffCheckConst.SUCCESS, response.data));
        })
        .catch(function(error: any) {
          self.showAxiosError('getBalanceRecords', error);
          dispatch(fetchedFunc(MoffCheckConst.ERROR, error));
        });
    };
  }

  /**
   * バランス測定データ.
   * (開眼片足立ち).
   * 最終利用日.
   *
   * @param {string} uniqueId
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffCheck
   */
  public getBalanceRecordsLatest(
    uniqueId: string, requestFunc: Function, fetchedFunc: Function): any {
    // const self = this;
    const url = `/check/balance/${uniqueId}/records/latest`;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get(url)
        .then(function(response: any) {
          dispatch(fetchedFunc(MoffCheckConst.SUCCESS, response.data.history));
        })
        .catch(function(error: any) {
          // 404のとき無視.
          // self.showAxiosError('getBalanceRecordsLatest', error);
          dispatch(fetchedFunc(MoffCheckConst.ERROR, error));
        });
    };
  }

  /**
   * 身体測定測定データ.
   * 指定の1件を取得.
   *
   * @param {string} uniqueId
   * @param {number} sequence
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffCheck
   */
  public getBodyRecords(
    uniqueId: string, sequence: number, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    const url = `/check/body/${uniqueId}/records/${sequence}`;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get(url)
        .then(function(response: any) {
          dispatch(fetchedFunc(MoffCheckConst.SUCCESS, response.data.history));
        })
        .catch(function(error: any) {
          self.showAxiosError('getBodyRecords', error);
          dispatch(fetchedFunc(MoffCheckConst.ERROR, error));
        });
    };
  }

  /**
   * 身体測定測定データ.
   * 件数を取得.
   *
   * @param {string} uniqueId
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffCheck
   */
  public getBodyRecordsCount(
    uniqueId: string, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    const url = `/check/body/${uniqueId}/records`;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get(url)
        .then(function(response: any) {
          dispatch(fetchedFunc(MoffCheckConst.SUCCESS, response.data.history));
        })
        .catch(function(error: any) {
          self.showAxiosError('getBodyRecordsCount', error);
          dispatch(fetchedFunc(MoffCheckConst.ERROR, error));
        });
    };
  }

  /**
   * エラーポップアップ.
   * apiNameはどのAPIによるものか.
   *
   * @private
   * @param {string} apiName
   * @param {*} error
   * @memberof MoffCheck
   */
  private showAxiosError(apiName: string, error: any) {
    if (error.toString().indexOf('Network Error') !== -1) {
      const errorMessage = '通信エラーが発生しました。インターネット接続が出来ることを確認してから再度ページを読み込み直して下さい。';
      alert(errorMessage);
    } else if (error.toString().indexOf('timeout') !== -1) {
      const errorMessage = '通信エラーが発生しました。インターネット接続が良好な環境で、再度ページを読み込み直して下さい。';
      alert(errorMessage);
    }

    // ログインへ.
    window.location.href = '/';
  }
}


// WEBPACK FOOTER //
// ./src/api/MoffCheck.tsx